import { PublicPartnerResponse } from '@/features/partners/types/partners.types';
import { cn } from '@/lib/utils';
import { OptimizedImage } from '@/components/ui/OptimizedImage';

interface PartnerCardProps {
  partner: PublicPartnerResponse;
  className?: string;
  categoryName?: string;
}

export function PartnerCard({ partner, className, categoryName }: PartnerCardProps) {
  const CardWrapper = partner.websiteUrl ? 'a' : 'div';
  const wrapperProps = partner.websiteUrl
    ? {
        href: partner.websiteUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': `Visit ${partner.name} website (opens in new tab)`,
      }
    : {};

  return (
    <CardWrapper
      {...wrapperProps}
      className={cn(
        'group block rounded-xl border border-border bg-card shadow-card',
        'p-6 transition-all duration-200',
        'hover:-translate-y-1 hover:border-primary/20 hover:shadow-card-hover',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className
      )}
    >
      {/* Logo */}
      <div className="relative mb-4 flex aspect-[3/2] items-center justify-center overflow-hidden rounded-lg bg-secondary">
        {partner.logoUrl ? (
          <OptimizedImage
            src={partner.logoUrl}
            alt={`${partner.name} logo`}
            className="w-full h-full object-contain p-4 mix-blend-multiply"
            containerClassName="w-full h-full"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
        ) : (
          <div className="text-4xl font-bold text-muted-foreground/45">
            {partner.name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        <h3 className="mb-1 text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
          {partner.name}
        </h3>

        {categoryName && (
          <span className="mb-2 inline-block rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {categoryName}
          </span>
        )}

        {partner.description && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {partner.description}
          </p>
        )}
      </div>

      {/* External link indicator */}
      {partner.websiteUrl && (
        <div className="mt-4 flex items-center text-sm text-primary opacity-0 transition-opacity group-hover:opacity-100">
          <span>Visit website</span>
          <svg
            className="ml-1 w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </div>
      )}
    </CardWrapper>
  );
}

export default PartnerCard;
